import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  BarChart3,
  Users,
  TrendingUp,
  Grid,
  Settings,
  Database,
  Target,
  DollarSign,
  Package
} from 'lucide-react';

import SupplyViewPage from './SupplyViewPage';
import DemandViewPage from './DemandViewPage';
import UtilizationAnalysisPage from './UtilizationAnalysisPage';
import AssignmentMapPage from './AssignmentMapPage';
import AdminPanelPage from './AdminPanelPage';
import PackageManagementPage from './PackageManagementPage';
import { OpsDashboardProvider, useOpsDashboard } from '@/context/OpsDashboardContext';

interface OverviewData {
  supply_health: number;
  supply_health_label: string;
  demand_pipeline: number;
  demand_pipeline_label: string;
  revenue_target: string;
  revenue_target_label: string;
  launch_window: string;
  launch_window_label: string;
}

const OpsDashboardContent: React.FC = () => {
  const [activeTab, setActiveTab] = useState('supply');
  const [overviewData, setOverviewData] = useState<OverviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const { refreshTriggers } = useOpsDashboard();

  const fetchOverviewData = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
      const response = await fetch(`${apiBaseUrl}/api/v1/ops-dashboard/overview`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setOverviewData(data);
      } else {
        console.error('Failed to fetch overview data');
      }
    } catch (error) {
      console.error('Error fetching overview data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOverviewData();
  }, []);

  // Refresh overview data when refresh trigger changes
  useEffect(() => {
    if (refreshTriggers.overview > 0) {
      fetchOverviewData();
    }
  }, [refreshTriggers.overview]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Marketplace Ops Dashboard</h1>
              <p className="text-gray-600 mt-1">
                Supply, demand, utilization, and assignment management for the managed marketplace
              </p>
            </div>
            <div className="flex items-center gap-4">
              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                Live Data
              </Badge>
              <div className="text-sm text-gray-500">
                Target Launch: March 1-15, 2026
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          {/* Navigation Tabs */}
          <div className="bg-white rounded-lg border border-gray-200 p-1">
            <TabsList className="grid w-full grid-cols-6 bg-transparent">
              <TabsTrigger
                value="supply"
                className="flex items-center gap-2 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700"
              >
                <Database className="h-4 w-4" />
                Supply View
              </TabsTrigger>
              <TabsTrigger
                value="demand"
                className="flex items-center gap-2 data-[state=active]:bg-green-50 data-[state=active]:text-green-700"
              >
                <Users className="h-4 w-4" />
                Demand View
              </TabsTrigger>
              <TabsTrigger
                value="packages"
                className="flex items-center gap-2 data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700"
              >
                <Package className="h-4 w-4" />
                Packages
              </TabsTrigger>
              <TabsTrigger
                value="utilization"
                className="flex items-center gap-2 data-[state=active]:bg-purple-50 data-[state=active]:text-purple-700"
              >
                <TrendingUp className="h-4 w-4" />
                Utilization & Gap
              </TabsTrigger>
              <TabsTrigger
                value="assignments"
                className="flex items-center gap-2 data-[state=active]:bg-orange-50 data-[state=active]:text-orange-700"
              >
                <Grid className="h-4 w-4" />
                Assignment Map
              </TabsTrigger>
              <TabsTrigger
                value="admin"
                className="flex items-center gap-2 data-[state=active]:bg-gray-50 data-[state=active]:text-gray-700"
              >
                <Settings className="h-4 w-4" />
                Admin Panel
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Tab Content */}
          <TabsContent value="supply" className="space-y-6">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Database className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Supply View</h2>
                  <p className="text-gray-600">Total inventory and utilization at show and slot level</p>
                </div>
              </div>
              <SupplyViewPage />
            </div>
          </TabsContent>

          <TabsContent value="demand" className="space-y-6">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Users className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Demand View</h2>
                  <p className="text-gray-600">Brand pipeline, requirements, and preference signals</p>
                </div>
              </div>
              <DemandViewPage />
            </div>
          </TabsContent>

          <TabsContent value="packages" className="space-y-6">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-indigo-100 rounded-lg">
                  <Package className="h-6 w-6 text-indigo-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Package Management</h2>
                  <p className="text-gray-600">Create and manage curated packages for the brand marketplace</p>
                </div>
              </div>
              <PackageManagementPage />
            </div>
          </TabsContent>

          <TabsContent value="utilization" className="space-y-6">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <TrendingUp className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Utilization & Gap Analysis</h2>
                  <p className="text-gray-600">Revenue tracking, supply-demand gaps, and recovery analysis</p>
                </div>
              </div>
              <UtilizationAnalysisPage />
            </div>
          </TabsContent>

          <TabsContent value="assignments" className="space-y-6">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Grid className="h-6 w-6 text-orange-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Assignment Map</h2>
                  <p className="text-gray-600">Visual cross-reference of show and brand assignments</p>
                </div>
              </div>
              <AssignmentMapPage />
            </div>
          </TabsContent>

          <TabsContent value="admin" className="space-y-6">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-gray-100 rounded-lg">
                  <Settings className="h-6 w-6 text-gray-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Admin Panel</h2>
                  <p className="text-gray-600">Manual data entry, verification, and system configuration</p>
                </div>
              </div>
              <AdminPanelPage />
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Quick Stats Footer */}
      <div className="bg-white border-t border-gray-200 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Database className="h-5 w-5 text-blue-600" />
                <span className="text-sm font-medium text-gray-600">Supply Health</span>
              </div>
              <div className="text-2xl font-bold text-blue-600">
                {loading ? '...' : overviewData?.supply_health || '0'}
              </div>
              <div className="text-xs text-gray-500">
                {overviewData?.supply_health_label || 'Creator Projects'}
              </div>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Users className="h-5 w-5 text-green-600" />
                <span className="text-sm font-medium text-gray-600">Demand Pipeline</span>
              </div>
              <div className="text-2xl font-bold text-green-600">
                {loading ? '...' : overviewData?.demand_pipeline || '0'}
              </div>
              <div className="text-xs text-gray-500">
                {overviewData?.demand_pipeline_label || 'Brand Partners'}
              </div>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Target className="h-5 w-5 text-purple-600" />
                <span className="text-sm font-medium text-gray-600">Revenue Target</span>
              </div>
              <div className="text-2xl font-bold text-purple-600">
                {loading ? '...' : overviewData?.revenue_target || '$0M'}
              </div>
              <div className="text-xs text-gray-500">
                {overviewData?.revenue_target_label || 'Revenue Potential'}
              </div>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <BarChart3 className="h-5 w-5 text-orange-600" />
                <span className="text-sm font-medium text-gray-600">Launch Window</span>
              </div>
              <div className="text-2xl font-bold text-orange-600">
                {loading ? '...' : overviewData?.launch_window || 'TBD'}
              </div>
              <div className="text-xs text-gray-500">
                {overviewData?.launch_window_label || 'Active'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main component that wraps the content with the provider
const OpsDashboardPage: React.FC = () => {
  return (
    <OpsDashboardProvider>
      <OpsDashboardContent />
    </OpsDashboardProvider>
  );
};

export default OpsDashboardPage;